import { PlusIcon, UngroupIcon } from "lucide-react";
import React, { type FormEvent, useCallback, useMemo } from "react";
import { MenuTrigger, type Selection } from "react-aria-components";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { RoleCondition } from "@/components/RoleCondition";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Centered } from "@/components/ui/container";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label, TextArea, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { StatusIcon } from "@/components/ui/icons";
import { Loader } from "@/components/ui/loader";
import { Menu, MenuItem, MenuSection } from "@/components/ui/menu";
import { Modal } from "@/components/ui/modal";
import { Note } from "@/components/ui/note";
import { Text } from "@/components/ui/text";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import type { TAccessionDetail, THold, TTestOrder } from "@/lib/types";
import { mutationResponse } from "@/utils/trpc";
import { useTestOrderGroupBy } from "../../store";
import { useAccession } from "../AccessionProvider";
import { DetailSection } from "./Section";
import { useDialogState, useSetDialogState } from "./state";
import { TestOrderTree, type TestOrderTreeGroupBy } from "./TestOrderTree";

export function TestOrdersSection() {
  const { accession, holds, testOrders } = useAccession();
  const setShowAddTests = useSetDialogState("addTests");

  const [groupBy, setGroupBy] = useTestOrderGroupBy();

  const onGroupByChange = (selection: Selection) => {
    if (selection === "all") return;
    const key = Array.from(selection)[0];
    if (key) {
      setGroupBy(key as TestOrderTreeGroupBy);
    }
  };

  return (
    <>
      <DetailSection
        title="Test orders"
        actions={
          <>
            <PrelimAlert
              orders={testOrders || []}
              holds={holds}
              accession={accession}
            />
            <MenuTrigger>
              <TooltipTrigger>
                <Button variant="ghost" size="icon-sm">
                  <UngroupIcon />
                </Button>
                <Tooltip>Group by</Tooltip>
              </TooltipTrigger>
              <Menu className="min-w-40">
                <MenuSection
                  selectionMode="single"
                  selectedKeys={[groupBy]}
                  onSelectionChange={onGroupByChange}
                  title="Group by"
                >
                  <MenuItem id="none">None</MenuItem>
                  <MenuItem id="panel">Panel</MenuItem>
                  <MenuItem id="sample">Sample</MenuItem>
                </MenuSection>
              </Menu>
            </MenuTrigger>
            <RoleCondition
              roles={[
                "blis_lab_manager",
                "blis_lab_tech",
                "blis_lab_assistant",
              ]}
            >
              <TooltipTrigger>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onPress={() => setShowAddTests(true)}
                >
                  <PlusIcon />
                </Button>
                <Tooltip>Add tests</Tooltip>
              </TooltipTrigger>
            </RoleCondition>
          </>
        }
      >
        <TestOrderTree groupBy={groupBy} />
      </DetailSection>
      <CancelTestOrderModal />
      <DeleteTestOrderModal />
      <ClearTestResultModal />
      <RestoreTestOrderModal />
      <CancelTestResultModal />
      <RestoreTestResultModal />
      <CancelPanelModal />
      <RestorePanelModal />
      <DeletePanelModal />
      <AssignSampleModal />
    </>
  );
}

function PrelimAlert({
  orders,
  holds,
  accession,
}: {
  orders: TTestOrder[];
  holds: THold[];
  accession: TAccessionDetail;
}) {
  const results = orders.flatMap((o) => o.results);
  const finalResultsBools = results.map(
    (res) => res.status === "final" || res.status === "cancelled",
  );
  const hasResults = finalResultsBools.includes(true);
  const isPrelim = finalResultsBools.includes(false);
  const hasHold = holds.some((hold) => hold.isActive);

  if (accession.cancelled || !hasResults) {
    return null;
  }

  return (
    <Text size="2" muted className="flex items-center gap-2">
      {isPrelim || hasHold ? (
        <>
          <StatusIcon status="in_progress" className="size-4" />
          Preliminary results
        </>
      ) : (
        <>
          <StatusIcon status="final" className="size-4" />
          Final results
        </>
      )}
    </Text>
  );
}

function CancelTestOrderModal() {
  const [state, setState] = useDialogState("cancelTestOrder");

  const cancel = trpc.orders.cancel.useMutation();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!state?.order) {
      return;
    }

    const formData = new FormData(e.currentTarget);
    const reason = formData.get("reason") as string;

    return cancel.mutate(
      { id: state.order.id, reason },
      mutationResponse({
        errorTitle: "Error cancelling test order",
        successMessage: "Test order cancelled",
        onSuccess() {
          setState(null);
        },
      }),
    );
  };

  return (
    <Modal isOpen={!!state} onOpenChange={() => setState(null)}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Cancel test order</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form onSubmit={onSubmit} id="cancel-form">
            <TextField isRequired name="reason">
              <Label>Reason for cancelling</Label>
              <TextArea placeholder="Enter reason for cancelling" />
            </TextField>
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button
            isPending={cancel.isPending}
            isDisabled={cancel.isPending}
            type="submit"
            form="cancel-form"
            formTarget="cancel-form"
          >
            Submit
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

function ClearTestResultModal() {
  const [state, setState] = useDialogState("clearTestResult");

  const clear = trpc.results.clear.useMutation(
    mutationResponse({
      errorTitle: "Error clearing result",
      successMessage: "Result cleared",
      onSuccess() {
        setState(null);
      },
    }),
  );

  return (
    <ConfirmPrompt
      title="Clear test result"
      message="Are you sure you want to clear this test result?"
      onConfirm={() => {
        if (state?.resultId) {
          clear.mutate({ id: state.resultId });
        }
      }}
      open={!!state?.resultId}
      onOpenChange={() => setState(null)}
      loading={clear.isPending}
    />
  );
}

function RestoreTestOrderModal() {
  const [state, setState] = useDialogState("restoreTestOrder");

  const restoreOrder = trpc.orders.restore.useMutation(
    mutationResponse({
      errorTitle: "Error restoring test order",
      successMessage: "Test order restored",
      onSuccess() {
        setState(null);
      },
    }),
  );

  return (
    <ConfirmPrompt
      title="Restore test order"
      message="Are you sure you want to restore this test order?"
      onConfirm={() => {
        if (state?.orderId) {
          restoreOrder.mutate({ id: state.orderId });
        }
      }}
      open={!!state?.orderId}
      onOpenChange={() => setState(null)}
      loading={restoreOrder.isPending}
    />
  );
}

function CancelTestResultModal() {
  const [state, setState] = useDialogState("cancelTestResult");

  const cancelResult = trpc.results.cancel.useMutation(
    mutationResponse({
      errorTitle: "Error cancelling result",
      successMessage: "Result cancelled",
      onSuccess() {
        setState(null);
      },
    }),
  );

  return (
    <ConfirmPrompt
      title="Cancel result"
      message="Are you sure you want to cancel this result?"
      onConfirm={() => {
        if (state?.resultId) {
          cancelResult.mutate({ id: state.resultId });
        }
      }}
      open={!!state?.resultId}
      onOpenChange={(flag) => setState(flag ? state : null)}
      loading={cancelResult.isPending}
    />
  );
}

function RestoreTestResultModal() {
  const [state, setState] = useDialogState("restoreTestResult");

  const restoreResult = trpc.results.restore.useMutation(
    mutationResponse({
      errorTitle: "Error restoring result",
      successMessage: "Result restored",
      onSuccess() {
        setState(null);
      },
    }),
  );

  return (
    <ConfirmPrompt
      title="Restore result"
      message="Are you sure you want to restore this result?"
      onConfirm={() => {
        if (state?.resultId) {
          restoreResult.mutate({ id: state.resultId });
        }
      }}
      open={!!state?.resultId}
      onOpenChange={() => setState(null)}
      loading={restoreResult.isPending}
    />
  );
}

function DeleteTestOrderModal() {
  const [state, setState] = useDialogState("deleteTestOrder");

  const deleteOrder = trpc.orders.delete.useMutation(
    mutationResponse({
      errorTitle: "Error deleting test order",
      successMessage: "Test order deleted",
      onSuccess() {
        setState(null);
      },
    }),
  );
  return (
    <ConfirmPrompt
      danger
      title="Delete test order"
      message="Are you sure you want to delete this test order? This action cannot be undone."
      onConfirm={() => {
        if (state?.orderId) {
          deleteOrder.mutate({ id: state.orderId });
        }
      }}
      open={!!state?.orderId}
      onOpenChange={() => setState(null)}
      loading={deleteOrder.isPending}
    />
  );
}

function CancelPanelModal() {
  const [state, setState] = useDialogState("cancelPanel");

  const cancel = trpc.orders.cancelPanel.useMutation();

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!state?.instructionId) {
      return;
    }

    const formData = new FormData(e.currentTarget);
    const reason = formData.get("reason") as string;

    return cancel.mutate(
      { instructionId: state.instructionId, reason },
      mutationResponse({
        errorTitle: "Error cancelling panel",
        successMessage: "Panel cancelled",
        onSuccess() {
          setState(null);
        },
      }),
    );
  };

  return (
    <Modal isOpen={!!state} onOpenChange={() => setState(null)}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Cancel panel</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form onSubmit={onSubmit} id="cancel-form">
            <TextField isRequired name="reason">
              <Label>Reason for cancelling</Label>
              <TextArea placeholder="Enter reason for cancelling" />
            </TextField>
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button
            isPending={cancel.isPending}
            isDisabled={cancel.isPending}
            type="submit"
            form="cancel-form"
            formTarget="cancel-form"
          >
            Submit
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}

function RestorePanelModal() {
  const [state, setState] = useDialogState("restorePanel");

  const restorePanel = trpc.orders.restorePanel.useMutation(
    mutationResponse({
      errorTitle: "Error restoring panel",
      successMessage: "Panel restored",
      onSuccess() {
        setState(null);
      },
    }),
  );

  return (
    <ConfirmPrompt
      title="Restore panel"
      message="Are you sure you want to restore this panel?"
      onConfirm={() => {
        if (state?.instructionId) {
          restorePanel.mutate({ instructionId: state.instructionId });
        }
      }}
      open={!!state?.instructionId}
      onOpenChange={() => setState(null)}
      loading={restorePanel.isPending}
    />
  );
}

function DeletePanelModal() {
  const [state, setState] = useDialogState("deletePanel");

  const deletePanel = trpc.orders.deletePanel.useMutation(
    mutationResponse({
      errorTitle: "Error deleting panel",
      successMessage: "Panel deleted",
      onSuccess() {
        setState(null);
      },
    }),
  );
  return (
    <ConfirmPrompt
      danger
      title="Delete panel"
      message="Are you sure you want to delete this panel? This action cannot be undone."
      onConfirm={() => {
        if (state?.instructionId) {
          deletePanel.mutate({ instructionId: state.instructionId });
        }
      }}
      open={!!state?.instructionId}
      onOpenChange={() => setState(null)}
      loading={deletePanel.isPending}
    />
  );
}

function AssignSampleModal() {
  const [state, setState] = useDialogState("assignSample");
  const { samples } = useAccession();
  const [selectedSamples, setSelectedSamples] = React.useState<number[] | null>(
    state?.currentSampleIds || null,
  );

  const assignmentPreferences = trpc.orders.assignmentPreferences.useQuery(
    { orderId: state?.orderId || 0 },
    { enabled: !!state?.orderId },
  );

  React.useEffect(() => {
    if (state?.currentSampleIds) {
      setSelectedSamples(state.currentSampleIds);
    }
  }, [state?.currentSampleIds]);

  const assignSample = trpc.orders.assignSamples.useMutation(
    mutationResponse({
      errorTitle: "Error assigning sample",
      successMessage: "Sample assigned",
      onSuccess() {
        setState(null);
      },
    }),
  );

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (state?.orderId && selectedSamples) {
      assignSample.mutate({
        orderId: state.orderId,
        sampleIds: selectedSamples,
      });
    }
  };

  const selectionMode = ["AUTO_ASSIGN_PRIORITY", "ORDER_PER_SAMPLE"].includes(
    assignmentPreferences.data?.testSpec.sampleAssignmentType ?? "",
  )
    ? "single"
    : "multiple";

  const selectAllState = useMemo(() => {
    if (selectedSamples?.length === samples.length) {
      return "all";
    }
    if (selectedSamples?.length === 0) {
      return "none";
    }
    return "some";
  }, [selectedSamples, samples]);

  const onSelectionChange = useCallback(
    (keys: Selection) => {
      if (keys === "all") {
        setSelectedSamples(samples.map((s) => s.id));
      } else {
        setSelectedSamples(Array.from(keys) as number[]);
      }
    },
    [samples],
  );

  return (
    <Modal isOpen={!!state} onOpenChange={() => setState(null)}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Assign sample(s)</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {assignmentPreferences.isPending && (
            <Centered>
              <Loader />
            </Centered>
          )}
          {assignmentPreferences.isError && (
            <Centered>
              <Note intent="warning">Error loading assignment preferences</Note>
            </Centered>
          )}
          {assignmentPreferences.isSuccess && assignmentPreferences.data && (
            <Form onSubmit={onSubmit} id="assign-form">
              {selectionMode === "multiple" && (
                <Checkbox
                  className="mb-2 mx-3"
                  isSelected={selectAllState === "all"}
                  isIndeterminate={selectAllState === "some"}
                  onChange={(checked) => {
                    if (checked) {
                      setSelectedSamples(samples.map((s) => s.id));
                    } else {
                      setSelectedSamples([]);
                    }
                  }}
                >
                  Select all
                </Checkbox>
              )}
              <GridList
                items={samples}
                aria-label="Samples"
                selectedKeys={selectedSamples ?? []}
                onSelectionChange={onSelectionChange}
                selectionMode={selectionMode}
              >
                {(item) => (
                  <GridListItem id={item.id} textValue={item.displayId}>
                    <div className="flex items-center gap-2">
                      <Badge size="sm" variant="outline">
                        {item.displayId}
                      </Badge>
                      {item.tube?.name}
                    </div>
                  </GridListItem>
                )}
              </GridList>
            </Form>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            isPending={assignSample.isPending}
            isDisabled={
              assignmentPreferences.isPending ||
              assignmentPreferences.isError ||
              assignSample.isPending ||
              !selectedSamples
            }
            type="submit"
            form="assign-form"
            formTarget="assign-form"
          >
            Submit
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
